import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';

// MUI
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';

// Contexts e Providers
import { ThemeProviderComponent } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ConnectivityProvider } from './contexts/ConnectivityProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WindowManagerProvider } from './contexts/WindowManagerContext';
import { SyncProvider } from './contexts/SyncContext';

import { UnsavedChangesProvider, useUnsavedChanges } from './contexts/UnsavedChangesContext';

// Componentes
import { ProtectedRoute } from './components/ProtectedRoute';
import WindowManager from './components/WindowManager';

// Páginas
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import ClienteList from './pages/cliente/list';
import ClienteEdit from './pages/cliente/edit';

import ListaPrecoList from './pages/listaPreco/listaPrecoList';
import LandingPage from './pages/landingPage/list';
import NFEditorComplete from './pages/nfeEntrada/list';
import AlterarSenha from './pages/alterarSenha/alterarSenha';
import PrintablePedidoPage from './pages/pedido_mobile/PrintPedido';
import EscalaVeiculos from './pages/escalas/EscalaVeiculos';
import SelecaoMatriz from './pages/selecaoMatriz/selecaoMatriz';
import NfeList from './pages/nfe/list';
import ListaFornecedores from './pages/pessoa/forcenedor/list';
import Cliente from './pages/pessoa/cliente/list';
import ListaVendedor from './pages/pessoa/vendedor/list';
import ListaFuncionario from './pages/pessoa/funcionario/list';
import ListaTransportadora from './pages/pessoa/transportadora/list';
import ListaPessoas from './pages/pessoa/pessoa';
import ProdutoList from './pages/produto/list';
import EntradaList from './pages/entrada/list';
import MenuUsuarioList from './pages/usuario/list';
import MatrizFilialList from './pages/matriz/list';
import EstNotaFiscalEntradaEdit from './pages/entrada/edit/EstNotaFiscalEntradaEdit';
import ProdutoForm from './pages/produto/edit';
import DynamicList from './components/DynamicList';
import PessoaEdit from './pages/pessoa/pessoaEdit';
import PedidosList from './pages/pedido_mobile/PedidosList';
import PedidosEdit from './pages/pedido_mobile/edit/PedidosEdit';

import HomePagePrint from './pages/HomePagePrint';
import SuporteNFESIC from './pages/suporte/nfe/list';

import TelaDinamica from './components/DynamicRel';
import PedidoMobilePage from './pages/pedido_mobile/PrintPedidoMobile';
import ProjectBoard from './pages/atividades/list';




/**
 * Esse componente só serve para efetuar o logout
 * e redirecionar para a página de login.
 */
function Logout() {
  const { logout } = useAuth();

  useEffect(() => {
    logout();
  }, [logout]);

  return <Navigate to="/login" replace />;
}

/**
 * Componente principal que gerencia as rotas e o WindowManager.
 */
function AppRoutes() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { hasUnsavedChanges } = useUnsavedChanges();

  // Verifica se a rota começa com /windows
  const isWindowRoute = location.pathname.startsWith('/windows');

  // Redirecionamento com confirmação
  useEffect(() => {

  }, [location.pathname, navigate, hasUnsavedChanges]);

  // Contagem regressiva para habilitar o botão
  const startCountdown = () => {
    setIsButtonEnabled(false);
    setCountdown(5);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsButtonEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    navigate(pendingNavigation, { replace: true });
  };



  // Rotas padrão (não /windows)
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/home"
        element={
          <ProtectedRoute>
             <SyncProvider>
              
            <WindowManagerProvider>
              <HomePage />
            </WindowManagerProvider>

            </SyncProvider>
          </ProtectedRoute>
        }
      />
        <Route
        path="/home/printPedido/:idPedido"
        element={
          <ProtectedRoute>
             <SyncProvider>
              
            <WindowManagerProvider>
              <HomePagePrint />
            </WindowManagerProvider>

            </SyncProvider>
          </ProtectedRoute>
        }
      />

      <Route
        path="/listaPreco"
        element={
          <ProtectedRoute>
            <ListaPrecoList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes"
        element={
          <ProtectedRoute>
            <ClienteList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/edit"
        element={
          <ProtectedRoute>
            <ClienteEdit mode="edit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/edit/:id"
        element={
          <ProtectedRoute>
            <ClienteEdit mode="edit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clientes/view/:id"
        element={
          <ProtectedRoute>
            <ClienteEdit mode="view" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pedidos"
        element={
          <ProtectedRoute>
            <PedidosList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos/edit"
        element={
          <ProtectedRoute>
            <PedidosEdit mode="edit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos/edit/:id"
        element={
          <ProtectedRoute>
            <PedidosEdit mode="edit" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos/view/:id"
        element={
          <ProtectedRoute>
            <PedidosEdit mode="view" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos/duplicar/:id"
        element={
          <ProtectedRoute>
            <PedidosEdit mode='duplicar'/>
          </ProtectedRoute>
        }
      />



      <Route
        path="/nfeEntrada"
        element={
          <ProtectedRoute>
            <NFEditorComplete />
          </ProtectedRoute>
        }
      />
      

      <Route
        path="/alterarSenha"
        element={
          <ProtectedRoute>
            <AlterarSenha />
          </ProtectedRoute>
        }
      />

<Route
        path="/escalas"
        element={
          <ProtectedRoute>
            <EscalaVeiculos />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/selecaoMatriz"
        element={
          <ProtectedRoute>
            <SelecaoMatriz />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/nfe"
        element={
          <ProtectedRoute>
            <NfeList />
          </ProtectedRoute>
        }

        
      />
     {/* FORNECEDOR */}
     <Route
        path="/pessoa/fornecedor"
        element={
          <ProtectedRoute>
            <ListaFornecedores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/fornecedor/edit"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComFornecedor" chavePrimaria="idFornecedor"  tituloTela='Fornecedor' />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/fornecedor/edit/:id"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComFornecedor" chavePrimaria="idFornecedor"  tituloTela='Fornecedor' />
          </ProtectedRoute>
        }
      />

      {/* CLIENTE */}
      <Route
        path="/pessoa/cliente"
        element={
          <ProtectedRoute>
            <Cliente />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/cliente/edit"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComCliente" chavePrimaria="idCliente"  tituloTela='Cliente' />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/cliente/edit/:id"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComCliente" chavePrimaria="idCliente"  tituloTela='Cliente'/>
          </ProtectedRoute>
        }
      />

      {/* VENDEDOR */}
      <Route
        path="/pessoa/vendedor"
        element={
          <ProtectedRoute>
            <ListaVendedor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/vendedor/edit"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComVendedor" chavePrimaria="idVendedor"  tituloTela='Vendedor'/>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/vendedor/edit/:id"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComVendedor" chavePrimaria="idVendedor"  tituloTela='Vendedor'/>
          </ProtectedRoute>
        }
      />

      {/* FUNCIONÁRIO */}
      <Route
        path="/pessoa/funcionario"
        element={
          <ProtectedRoute>
            <ListaFuncionario />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/funcionario/edit"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComFuncionario" chavePrimaria="idFuncionario" tituloTela='Funcionario' />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/funcionario/edit/:id"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComFuncionario" chavePrimaria="idFuncionario"  tituloTela='Funcionario'/>
          </ProtectedRoute>
        }
      />

      {/* TRANSPORTADORA */}
      <Route
        path="/pessoa/transportadora"
        element={
          <ProtectedRoute>
            <ListaTransportadora />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/transportadora/edit"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComTransportadora" chavePrimaria="idTransportadora" tituloTela='Transportadora'  />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pessoa/transportadora/edit/:id"
        element={
          <ProtectedRoute>
            <PessoaEdit mode="edit" objeto="ComTransportadora" chavePrimaria="idTransportadora" tituloTela='Transportadora'/>
          </ProtectedRoute>
        }
      /> 
<Route
        path="/pessoa"
        element={
          <ProtectedRoute>
            <ListaPessoas />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/produto"
        element={
          <ProtectedRoute>
            <ProdutoList />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/produto/edit/:id"
        element={
          <ProtectedRoute>
            <ProdutoForm />
          </ProtectedRoute>
        }

        
      />


<Route
        path="/entrada"
        element={
          <ProtectedRoute>
            <EntradaList />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/usuario"
        element={
          <ProtectedRoute>
            <MenuUsuarioList />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/matriz"
        element={
          <ProtectedRoute>
            <MatrizFilialList />
          </ProtectedRoute>
        }

        
      />
      <Route
        path="/entrada/edit/:id"
        element={
          <ProtectedRoute>
            <EstNotaFiscalEntradaEdit />
          </ProtectedRoute>
        }

        
      />
  <Route
        path="/entrada/edit"
        element={
          <ProtectedRoute>
            <EstNotaFiscalEntradaEdit />
          </ProtectedRoute>
        }

        
      />



<Route
        path="/grupoProdutos"
        element={
          <ProtectedRoute>
            <DynamicList 
        entityName="EstGrupoProduto" 
        fields="idGrupoProduto,descricaoGrupo" 
        filter="descricaoGrupo" 
        label="codigo,descrição"
        edit="idGrupoProduto,descricaoGrupo"
        desabilitado="idGrupoProduto"
        labelEdit="Codigo,Descricao"
        titulo="Sub Grupo de Produtos"
        mostrarTitulo="true"
        defaultOrder="idGrupoProduto"
        order="DESC"
      />
          </ProtectedRoute>
        }

        
      />


<Route
        path="/subGrupoProduto"
        element={
          <ProtectedRoute>
          <DynamicList
  entityName="EstSubGrupoProduto"
  fields="idSubgrupoProduto,descricao"
  filter="descricao"
  label="codigo,descrição"
  edit="idSubgrupoProduto,descricao"
  desabilitado="idSubgrupoProduto"
  labelEdit="Codigo,Descricao"
  titulo="Sub Grupo de Produtos"
  mostrarTitulo="true"
/>

          </ProtectedRoute>
        }

        
      />


<Route
        path="/suporte/nfe/:idNfe"
        element={
          <ProtectedRoute>
            <SuporteNFESIC />
          </ProtectedRoute>
        }

        
      />

<Route
        path="/relatorios/DynamicRel"
        element={
          <ProtectedRoute>
            <TelaDinamica />
          </ProtectedRoute>
        }

        
      />


<Route
        path="/trello"
        element={
          <ProtectedRoute>
            <ProjectBoard />
          </ProtectedRoute>
        }

        
      />



      <Route path="/pedidos/imprimir/:id" element={<PrintablePedidoPage />} />
      <Route path="/pedido/:idPedidoMobile" element={<PedidoMobilePage />} />

      <Route path="/logout" element={<Logout />} />

      {/* Rota wildcard para redirecionar se a rota não existir */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Componente principal da aplicação.
 */
function App() {
  return (
    <ThemeProviderComponent>
      <NotificationProvider>
      <Router>
        <AuthProvider>
          <ConnectivityProvider>
           
              <UnsavedChangesProvider>
              
                  <AppRoutes />
                
              </UnsavedChangesProvider>
          
          </ConnectivityProvider>
        </AuthProvider>
        </Router>
      </NotificationProvider>
    </ThemeProviderComponent>
  );
}

export default App;